import { ReactNode, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import ApiService from "@/services/ApiService";
import { AxiosError } from "axios";
export type Page<T> = {
  data: T[];
  total: number;
  current_page: number;
  last_page: number;
};
export function message(error: unknown) {
  const response = (
    error as AxiosError<{ message?: string; errors?: Record<string, string[]> }>
  ).response?.data;
  return response?.errors
    ? Object.values(response.errors).flat().join(" / ")
    : (response?.message ??
        (error instanceof Error ? error.message : "操作に失敗しました。"));
}
export function useData<T>(url: string, params: object = {}) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);
  const key = JSON.stringify(params);
  useEffect(() => {
    if (!url) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setData(null);
    ApiService.get(url, { params: JSON.parse(key), signal: controller.signal })
      .then((r) => {
        if (!controller.signal.aborted) setData(r.data.data);
      })
      .catch((e) => {
        if (!controller.signal.aborted) setError(message(e));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [url, key, revision]);
  return { data, error, loading, reload: () => setRevision((n) => n + 1) };
}
export function Frame({
  title,
  children,
  description,
}: {
  title: string;
  children: ReactNode;
  description?: string;
}) {
  return (
    <div className="max-w-6xl mx-auto p-3 md:p-6 space-y-6">
      <header>
        <h1 className="text-2xl font-bold">{title}</h1>
        {description && (
          <p className="text-sm text-muted-foreground mt-2">{description}</p>
        )}
      </header>
      {children}
    </div>
  );
}
export function Panel({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  return (
    <section className="border rounded-xl bg-card p-5 space-y-4">
      {title && <h2 className="text-lg font-semibold">{title}</h2>}
      {children}
    </section>
  );
}
export function Feedback({
  error,
  notice,
}: {
  error?: string;
  notice?: string;
}) {
  return (
    <>
      {error && (
        <p role="alert" className="p-3 border rounded-lg text-destructive">
          {error}
        </p>
      )}
      {notice && (
        <p
          role="status"
          className="p-3 border rounded-lg text-green-700 dark:text-green-300"
        >
          {notice}
        </p>
      )}
    </>
  );
}
export function LoadState({
  loading,
  error,
  reload,
}: {
  loading: boolean;
  error: string;
  reload: () => void;
}) {
  return loading ? (
    <p role="status">読み込み中…</p>
  ) : error ? (
    <div role="alert" className="space-y-3">
      <p className="text-destructive">{error}</p>
      <Button variant="outline" onClick={reload}>
        再試行
      </Button>
    </div>
  ) : null;
}
export function useMutation() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const run = async (
    action: () => Promise<unknown>,
    text = "保存しました。",
  ) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await action();
      setNotice(text);
      return true;
    } catch (failure) {
      setError(message(failure));
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, notice, run };
}
export function Pager({
  result,
  setPage,
}: {
  result: { total: number; current_page: number; last_page: number };
  setPage: (page: number) => void;
}) {
  return (
    <div className="flex justify-between items-center gap-3 text-sm">
      <span>
        {result.total}件 · {result.current_page} / {result.last_page}ページ
      </span>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={result.current_page <= 1}
          onClick={() => setPage(result.current_page - 1)}
        >
          前へ
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={result.current_page >= result.last_page}
          onClick={() => setPage(result.current_page + 1)}
        >
          次へ
        </Button>
      </div>
    </div>
  );
}
export const fieldClass =
  "w-full rounded-md border bg-background px-3 py-2 text-sm";
export const roles: Record<string, string> = {
  platform_admin: "プラットフォーム管理者",
  platform_support: "サポート",
  platform_auditor: "監査担当",
  organization_admin: "組織管理者",
  recruiter: "採用担当",
  viewer: "閲覧者",
};
export const statuses: Record<string, string> = {
  active: "応募受付",
  document_screening: "書類選考",
  interviewing: "面接",
  accepted: "採用",
  declined: "不採用",
};
export function japanDate(value?: string) {
  return value
    ? new Date(
        value.endsWith("Z") || /[+-]\d\d:\d\d$/.test(value)
          ? value
          : value.replace(" ", "T") + "Z",
      ).toLocaleString("ja-JP", { timeZone: "Asia/Tokyo" })
    : "—";
}
